import {
    ArrowBack,
    Assessment,
    BarChart,
    Download,
    FilterList,
    Groups,
    Insights,
    Psychology,
    Security,
    TrendingUp
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Tab,
    Tabs,
    Typography,
    useMediaQuery
} from "@mui/material";
import fileDownload from "js-file-download";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import insightsApi from "../../api/insightsApi";
import AskPrompta from "../../components/Insight/AskPrompta";
import Chatbot from "../../components/Insight/Chatbot";
import FiltersSection from "../../components/Insight/FiltersSection";
import GroupScoresSection from "../../components/Insight/GroupScoresSection";
import KeywordsSection from "../../components/Insight/KeywordsSection";
import LikertsSection from "../../components/Insight/LikertsSection";
import RisksSection from "../../components/Insight/RisksSection";
import StatsSection from "../../components/Insight/StatsSection";
import TopicsSection from "../../components/Insight/TopicsSection";

export default function InsightDetailsPage() {
    const intl = useIntl();
    // Note: Despite the name, surveyId here is actually the insight ID from the URL
    const { surveyId } = useParams();
    const navigate = useNavigate();
    const [insightData, setInsightData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [alert, setAlert] = useState(null);
    const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
    const [downloading, setDownloading] = useState(false);
    // Whether the "By group" tab exists. Decided from the project's group
    // registry (a cheap read) rather than the scores, which the tab fetches
    // itself when opened.
    const [showGroupsTab, setShowGroupsTab] = useState(false);

    const isMobile = useMediaQuery('(max-width: 600px)');

    // Transform top_terms object to keywords array format for backwards compatibility
    const transformTopTermsToKeywords = (data) => {
        if (!data || !data.results) return data;

        // If top_terms exists and keywords doesn't, transform it
        if (data.results.top_terms && !data.results.keywords) {
            const keywordsArray = Object.entries(data.results.top_terms).map(([word, termData]) => {
                // Replace null values with 0 in attr_sentiment
                const attr_sentiment = termData.attr_sentiment ?
                    Object.entries(termData.attr_sentiment).reduce((acc, [key, value]) => {
                        acc[key] = value === null ? 0 : value;
                        return acc;
                    }, {}) : {};

                return {
                    word,
                    frequency: termData.frequency || 0,
                    responses: termData.responses || [],
                    attr_sentiment,
                    // Add empty questions array for compatibility
                    questions: []
                };
            });

            return {
                ...data,
                results: {
                    ...data.results,
                    keywords: keywordsArray
                }
            };
        }

        return data;
    };

    useEffect(() => {
        loadInsightDetails();
    }, [surveyId]);

    const loadInsightDetails = async () => {
        try {
            setLoading(true);
            // surveyId is the report id from the URL. The By-group gate rides on
            // the report itself: `by_group_available` is true iff the report has
            // a stakeholder group with direct members (and grouping is enabled).
            // The BE owns that answer, so there is no group-registry read here —
            // and a payload without the flag just leaves the tab out.
            const data = await insightsApi.getInsightReport(surveyId);
            // const transformedData = transformTopTermsToKeywords(data);
            setShowGroupsTab(data?.by_group_available === true);
            setInsightData(data);
        } catch (error) {
            console.error("Error loading insight details:", error);
            setAlert({ type: "error", message: intl.formatMessage({ id: "insightDetails.load_failed", defaultMessage: "Failed to load insight details" }) });
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Helper to check if likert data has actual content
    const hasLikertData = (likertData) => {
        if (!likertData) return false;
        const { questions, responses, response_types } = likertData;
        return (
            (questions && questions.length > 0) ||
            (responses && responses.length > 0) ||
            (response_types && response_types.length > 0)
        );
    };

    const showLikertTab = hasLikertData(insightData?.results?.likert_skirt);

    // Risks tab is only shown when risk_filters actually carries options.
    const hasRiskFilters = (rf) =>
        rf != null && typeof rf === "object" && Object.keys(rf).length > 0;
    const showRiskTab = hasRiskFilters(insightData?.results?.risk_filters);

    const handleExport = () => {
        setDownloadDialogOpen(true);
    };

    const handleCloseDownloadDialog = () => {
        setDownloadDialogOpen(false);
    };

    const handleDownload = async (downloadType) => {
        if (!surveyId) return;

        try {
            setDownloading(true);
            // Fetch the report data using the same API
            const data = await insightsApi.getInsightReport(surveyId);
            let typeData = data;
            if (downloadType === 'likert') {
                typeData = data.results?.likert_skirt;
            } else if (downloadType === 'mcq') {
                typeData = data.results?.stats?.questions;
            } else if (downloadType === 'openText') {
                typeData = data.results?.topics;
            }

            // For now, all download types return the same JSON
            const jsonData = JSON.stringify(typeData, null, 2);

            // Generate filename based on download type
            const filenames = {
                all: `insight_report_all_data_${surveyId}_${Date.now()}.json`,
                likert: `insight_report_likert_scale_${surveyId}_${Date.now()}.json`,
                mcq: `insight_report_mcq_${surveyId}_${Date.now()}.json`,
                openText: `insight_report_open_text_${surveyId}_${Date.now()}.json`,
            };

            const filename = filenames[downloadType] || filenames.all;

            // Download the file
            fileDownload(jsonData, filename);

            setAlert({
                type: "success",
                message: intl.formatMessage({ id: "insight.download_started", defaultMessage: "Download started successfully" })
            });

            handleCloseDownloadDialog();
        } catch (error) {
            console.error("Error downloading report:", error);
            setAlert({
                type: "error",
                message: intl.formatMessage({ id: "insight.failed_to_download", defaultMessage: "Failed to download report" })
            });
        } finally {
            setDownloading(false);
        }
    };

    const handleJsonUpload = (jsonData) => {
        // const transformedData = transformTopTermsToKeywords(jsonData);
        setInsightData(jsonData);
        setAlert({ type: "success", message: intl.formatMessage({ id: "insightDetails.json_upload_success", defaultMessage: "JSON data uploaded successfully" }) });
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "calc(100vh - 64px)",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!insightData) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    <FormattedMessage id="insightDetails.data_not_found" defaultMessage="Insight data not found" />
                </Alert>
            </Box>
        );
    }

    // Tab definitions are built dynamically so optional tabs (Risks, Likerts)
    // simply drop out of the list instead of leaving index gaps to patch around.
    const mainTabs = [
        {
            key: "topics",
            label: intl.formatMessage({ id: "insightDetails.tab_topics", defaultMessage: "Topics" }),
            icon: <Psychology />,
            content: (
                <TopicsSection
                    topicsData={insightData.results?.topics}
                    pillarSentimentMeans={insightData.results?.pillar_sentiment_means}
                    statsData={insightData.results?.stats}
                    reportId={insightData.report_id}
                    reportName={insightData.name}
                    language={insightData.language}
                />
            ),
        },
        showRiskTab && {
            key: "risks",
            label: intl.formatMessage({ id: "insightDetails.tab_risks", defaultMessage: "Risks" }),
            icon: <Security />,
            content: (
                <RisksSection
                    reportId={insightData.report_id}
                    riskFilters={insightData.results?.risk_filters}
                    signalResults={insightData.signal_results}
                    language={insightData.language}
                />
            ),
        },
        showGroupsTab && {
            key: "groups",
            label: intl.formatMessage({ id: "insightDetails.tab_groups", defaultMessage: "By group" }),
            icon: <Groups />,
            // Scores are fetched by the section on mount, i.e. when the tab opens.
            content: <GroupScoresSection reportId={insightData.report_id} />,
        },
        {
            key: "keywords",
            label: intl.formatMessage({ id: "insightDetails.tab_keywords", defaultMessage: "Keywords" }),
            icon: <TrendingUp />,
            content: (
                <KeywordsSection
                    keywordsData={insightData.results?.top_terms}
                    statsData={insightData.results?.stats}
                    reportId={insightData.report_id}
                    reportName={insightData.name}
                />
            ),
        },
        {
            key: "filters",
            label: intl.formatMessage({ id: "insightDetails.tab_filters", defaultMessage: "Segmentation" }),
            icon: <FilterList />,
            content: (
                <FiltersSection
                    statsData={insightData.results?.stats}
                    topicsData={insightData.results?.topics}
                    likertData={insightData.results?.likert_skirt}
                />
            ),
        },
        {
            key: "engagement",
            label: intl.formatMessage({ id: "insightDetails.tab_engagement", defaultMessage: "Engagement" }),
            icon: <Assessment />,
            content: <StatsSection statsData={insightData.results?.stats} />,
        },
        showLikertTab && {
            key: "likerts",
            label: intl.formatMessage({ id: "insightDetails.tab_likerts", defaultMessage: "Likerts" }),
            icon: <BarChart />,
            content: (
                <LikertsSection
                    likertData={insightData.results?.likert_skirt}
                    likertSentiment={insightData.results?.likert_sentiment}
                />
            ),
        },
    ].filter(Boolean);

    const tabIndexByKey = (key) => mainTabs.findIndex((t) => t.key === key);
    const insightGenTab = mainTabs.length;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, mt: 2 }}>
                {insightData.name && (
                    <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, color: "#00438E", mb: 1.5, lineHeight: 1.3 }}
                    >
                        {insightData.name}
                    </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    {/* Left - Back Button */}
                    <IconButton onClick={() => navigate("/insights")} sx={{ flexShrink: 0 }}>
                        <ArrowBack />
                    </IconButton>

                    {/* Center - Key Metrics */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', flex: 1 }}>
                        {/* Responses */}
                        <Paper
                            onClick={() => setActiveTab(tabIndexByKey("topics"))}
                            elevation={0}
                            sx={{
                                px: 2,
                                py: 1,
                                textAlign: "center",
                                minWidth: 80,
                                background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(255, 152, 0, 0.2)'
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100', lineHeight: 1 }}>
                                {insightData.results?.stats?.responded || insightData.results?.stats?.completely_responded || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#f57c00', fontWeight: 500, fontSize: '0.7rem' }}>
                                <FormattedMessage id="insightDetails.responses" defaultMessage="Responses" />
                            </Typography>
                        </Paper>
                        {/* Demog */}
                        <Paper
                            onClick={() => setActiveTab(tabIndexByKey("filters"))}
                            elevation={0}
                            sx={{
                                px: 2,
                                py: 1,
                                textAlign: "center",
                                minWidth: 80,
                                background: 'linear-gradient(135deg, #E8F5F5 0%, #C8E6E6 100%)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(76 175 172 / 0.2)'
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', lineHeight: 1 }}>
                                {insightData.results?.stats?.questions?.length || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#558b2f', fontWeight: 500, fontSize: '0.7rem' }}>
                                <FormattedMessage id="insightDetails.filters" defaultMessage="Filters" />
                            </Typography>
                        </Paper>
                        {showLikertTab && (
                            <Paper
                                onClick={() => setActiveTab(tabIndexByKey("likerts"))}
                                elevation={0}
                                sx={{
                                    px: 2,
                                    py: 1,
                                    textAlign: "center",
                                    minWidth: 80,
                                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(33, 150, 243, 0.2)'
                                    },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565c0', lineHeight: 1 }}>
                                    {insightData.results?.likert_skirt?.questions?.length || 0}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 500, fontSize: '0.7rem' }}>
                                    <FormattedMessage id="insightDetails.likerts" defaultMessage="Likerts" />
                                </Typography>
                            </Paper>
                        )}
                        <Paper
                            onClick={() => setActiveTab(tabIndexByKey("keywords"))}
                            elevation={0}
                            sx={{
                                px: 2,
                                py: 1,
                                textAlign: "center",
                                minWidth: 80,
                                background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(0, 188, 212, 0.2)'
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#00838f', lineHeight: 1 }}>
                                {Object.keys(insightData.results?.top_terms || {}).length || 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#0097a7', fontWeight: 500, fontSize: '0.7rem' }}>
                                <FormattedMessage id="insightDetails.keywords" defaultMessage="Keywords" />
                            </Typography>
                        </Paper>
                        <Paper
                            onClick={() => setActiveTab(tabIndexByKey(showLikertTab ? "likerts" : "engagement"))}
                            elevation={0}
                            sx={{
                                px: 2,
                                py: 1,
                                textAlign: "center",
                                minWidth: 80,
                                background: 'linear-gradient(135deg, #E1E0FF 0%, #D7EFFB 100%)',
                                borderRadius: 2,
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(8 0 255 / 0.2)'
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#9200E6', lineHeight: 1 }}>
                                {insightData.results?.topics
                                    ? Object.values(insightData.results.topics).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0)
                                    : 0}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#8300F5', fontWeight: 500, fontSize: '0.7rem' }}>
                                <FormattedMessage id="insightDetails.topics" defaultMessage="Topics" />
                            </Typography>
                        </Paper>
                    </Box>

                    {/* Right - Export Button */}
                </Box>
            </Box>

            {/* Alert */}
            {alert && (
                <Alert
                    severity={alert.type}
                    sx={{ mb: 3 }}
                    onClose={() => setAlert(null)}
                >
                    {alert.message}
                </Alert>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3, overflowX: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 'max-content' }}>
                    <Tabs
                        value={activeTab < mainTabs.length ? activeTab : false}
                        onChange={(e, v) => handleTabChange(e, v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ minWidth: 'max-content' }}
                    >
                        {mainTabs.map((t) => (
                            <Tab key={t.key} label={isMobile ? "" : t.label} icon={t.icon} iconPosition="start" />
                        ))}
                    </Tabs>
                    <Tabs value={activeTab === insightGenTab ? 0 : false} onChange={(e) => handleTabChange(e, insightGenTab)}>
                        <Tab label={isMobile ? "" : intl.formatMessage({ id: "insightDetails.tab_insight_generation", defaultMessage: "Insight Generation" })} icon={<Insights />} iconPosition="start" />
                    </Tabs>
                </Box>
            </Box>

            {/* Tab Content */}
            <Box sx={{ mt: 3 }}>
                {mainTabs[activeTab]?.content}

                {/* Insight Generation Tab */}
                {activeTab === insightGenTab && <AskPrompta reportId={insightData.report_id} topicsData={insightData.results?.topics} hasRiskData={insightData.has_risk_data} />}
            </Box>

            {/* Download Options Dialog */}
            <Dialog
                open={downloadDialogOpen}
                onClose={handleCloseDownloadDialog}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        maxWidth: 320,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1, pt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                        <FormattedMessage id="insight.download_options" defaultMessage="Download Options" />
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 1, pb: 1 }}>
                    <List sx={{ py: 0 }}>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => handleDownload('all')}
                                disabled={downloading}
                                sx={{
                                    borderRadius: 1,
                                    mb: 0.5,
                                    py: 1,
                                    "&:hover": {
                                        bgcolor: "action.hover"
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={intl.formatMessage({ id: "insight.download_all_data", defaultMessage: "Download All Data" })}
                                    primaryTypographyProps={{ variant: "body2" }}
                                    secondary={intl.formatMessage({ id: "insight.complete_report_data", defaultMessage: "Complete report data" })}
                                    secondaryTypographyProps={{ variant: "caption" }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => handleDownload('likert')}
                                disabled={downloading}
                                sx={{
                                    borderRadius: 1,
                                    mb: 0.5,
                                    py: 1,
                                    "&:hover": {
                                        bgcolor: "action.hover"
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={intl.formatMessage({ id: "insight.download_likert_scale", defaultMessage: "Download Likert Scale" })}
                                    primaryTypographyProps={{ variant: "body2" }}
                                    secondary={intl.formatMessage({ id: "insight.likert_scale_responses", defaultMessage: "Likert scale responses" })}
                                    secondaryTypographyProps={{ variant: "caption" }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => handleDownload('mcq')}
                                disabled={downloading}
                                sx={{
                                    borderRadius: 1,
                                    mb: 0.5,
                                    py: 1,
                                    "&:hover": {
                                        bgcolor: "action.hover"
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={intl.formatMessage({ id: "insight.download_mcq", defaultMessage: "Download MCQ" })}
                                    primaryTypographyProps={{ variant: "body2" }}
                                    secondary={intl.formatMessage({ id: "insight.multiple_choice_questions", defaultMessage: "Multiple choice questions" })}
                                    secondaryTypographyProps={{ variant: "caption" }}
                                />
                            </ListItemButton>
                        </ListItem>
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => handleDownload('openText')}
                                disabled={downloading}
                                sx={{
                                    borderRadius: 1,
                                    py: 1,
                                    "&:hover": {
                                        bgcolor: "action.hover"
                                    }
                                }}
                            >
                                <ListItemText
                                    primary={intl.formatMessage({ id: "insight.download_open_text", defaultMessage: "Download Open Text Questions" })}
                                    primaryTypographyProps={{ variant: "body2" }}
                                    secondary={intl.formatMessage({ id: "insight.open_ended_responses", defaultMessage: "Open-ended responses" })}
                                    secondaryTypographyProps={{ variant: "caption" }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                    {downloading && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                            <CircularProgress size={20} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5, pt: 0 }}>
                    <Button onClick={handleCloseDownloadDialog} disabled={downloading} size="small">
                        <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Floating Chatbot */}
            {activeTab !== insightGenTab && <Chatbot reportId={insightData.report_id} />}
        </Box>
    );
}

